import type { FieldOption } from "@/lib/field-util";
import {
  COMPANY_SIZE_MAX,
  COMPANY_SIZE_MIN,
  PERSON_AGE_MAX,
  PERSON_AGE_MIN,
  PERSON_CHILDREN_MAX,
  PERSON_CHILDREN_MIN,
} from "@/lib/search-filters";

// The DATA MODEL behind the extended search's filter pills — what a facet is, and how to read one
// off a category's filter state. The pill UI that renders it lives in FilterBuilder.tsx.
//
// Kept apart from the component deliberately: a .tsx file that exports both a component and the
// constants around it breaks fast refresh (react-refresh/only-export-components), and every search
// category needs these helpers without needing the component.
//
// Originally the Teamplayer builder's internals, and generic now because a second category needed
// them: nothing here ever knew what a person was — every read already went through a
// `Record<string, …>` cast on the field descriptor.

export interface FilterField {
  key: string;
  labelId: string;
  /**
   * `textList` is `text` that keeps several values: type a term, press Enter, it becomes a chip.
   * Used where the values are open-ended so there is no option list to pick from, but one value is
   * not enough — company expertise, where a searcher wants "welding OR control systems".
   */
  kind: "text" | "range" | "multi" | "textList";
  /** range: the state keys holding the two bounds. */
  min?: string;
  max?: string;
  /** range: the bounds themselves. A bound left at lo/hi counts as "not set". */
  lo?: number;
  hi?: number;
  /** multi: the state key holding the selected ids. */
  field?: string;
  /** multi: which list in `optionsByKey` to offer. */
  optionsKey?: string;
  searchable?: boolean;
}

export type OptionsByKey = Record<string, readonly FieldOption[]>;

export function isFieldActive(field: FilterField, f: object): boolean {
  if (field.kind === "text") {
    const v = (f as unknown as Record<string, string>)[field.key];
    return Boolean(v?.trim());
  }
  if (field.kind === "textList") {
    return ((f as unknown as Record<string, readonly string[]>)[field.field!] ?? []).length > 0;
  }
  if (field.kind === "range") {
    const min = (f as unknown as Record<string, number>)[field.min!] ?? field.lo!;
    const max = (f as unknown as Record<string, number>)[field.max!] ?? field.hi!;
    return min > field.lo! || max < field.hi!;
  }
  const arr = (f as unknown as Record<string, readonly string[]>)[field.field!];
  return (arr ?? []).length > 0;
}

export function clearField<T extends object>(field: FilterField, f: T): T {
  if (field.kind === "text") return { ...f, [field.key]: "" };
  if (field.kind === "textList") return { ...f, [field.field!]: [] };
  if (field.kind === "range") return { ...f, [field.min!]: field.lo!, [field.max!]: field.hi! };
  return { ...f, [field.field!]: [] };
}

export function fieldSummary(field: FilterField, f: object, opts: OptionsByKey): string {
  if (field.kind === "text") {
    return (((f as unknown as Record<string, string>)[field.key]) ?? "").trim();
  }
  if (field.kind === "textList") {
    return ((f as unknown as Record<string, readonly string[]>)[field.field!] ?? []).join(", ");
  }
  if (field.kind === "range") {
    return `${(f as unknown as Record<string, number>)[field.min!] ?? field.lo!}\u2013${(f as unknown as Record<string, number>)[field.max!] ?? field.hi!}`;
  }
  const lookup = opts[field.optionsKey!] ?? [];
  return ((f as unknown as Record<string, readonly string[]>)[field.field!] ?? [])
    .map((id) => lookup.find((o) => o.value === id)?.label ?? id)
    .join(", ");
}

// ---------------------------------------------------------------------------------------------
// The catalogues: which facets each search category offers.
// ---------------------------------------------------------------------------------------------

export const PERSON_FIELDS: readonly FilterField[] = [
  { key: "firstName", labelId: "search.fieldFirstName", kind: "text" },
  { key: "lastName", labelId: "search.fieldLastName", kind: "text" },
  { key: "age", labelId: "search.fieldAge", kind: "range", min: "ageMin", max: "ageMax", lo: PERSON_AGE_MIN, hi: PERSON_AGE_MAX },
  { key: "gender", labelId: "search.fieldGender", kind: "multi", field: "genderIds", optionsKey: "gender" },
  { key: "marital", labelId: "search.fieldMaritalStatus", kind: "multi", field: "maritalStatusIds", optionsKey: "marital" },
  { key: "children", labelId: "search.fieldChildren", kind: "range", min: "childCountMin", max: "childCountMax", lo: PERSON_CHILDREN_MIN, hi: PERSON_CHILDREN_MAX },
  { key: "city", labelId: "search.fieldCity", kind: "text" },
  { key: "country", labelId: "search.fieldCountry", kind: "multi", field: "countryIds", optionsKey: "country", searchable: true },
  { key: "citizenship", labelId: "search.fieldCitizenship", kind: "multi", field: "citizenshipIds", optionsKey: "country", searchable: true },
  { key: "nationality", labelId: "search.fieldNationality", kind: "text" },
  { key: "languages", labelId: "search.fieldLanguages", kind: "multi", field: "languageCodes", optionsKey: "language", searchable: true },
  { key: "expertise", labelId: "search.fieldExpertise", kind: "text" },
  { key: "jobTitle", labelId: "search.fieldJobTitle", kind: "text" },
  { key: "department", labelId: "search.fieldDepartment", kind: "text" },
  { key: "industry", labelId: "search.fieldIndustry", kind: "multi", field: "industryIds", optionsKey: "industry", searchable: true },
  { key: "location", labelId: "search.fieldLocation", kind: "text" },
];

// v1 guess: which pill types to surface at the top of the "+ Filter hinzufügen" menu.
// Revise once telemetry is available – single source of truth.
export const PERSON_FREQUENT_FIELD_KEYS: readonly string[] = [
  "industry", "department", "expertise", "location", "languages",
];

/**
 * The Companies category's facets.
 *
 * WHAT IS AND IS NOT HERE IS A DECISION, NOT AN OMISSION — and since concept v6 it is the
 * concept's decision rather than this file's.
 * the company search names three axes for FINDING a company — name,
 * expertise and industry — and deliberately keeps the descriptive prose out of the index. §2 (v6)
 * adds the rule this list follows: a filter narrows only WITHIN those axes, with exactly one named
 * exception — size. Size is never indexed and makes no company findable; it only narrows an
 * already-found set, the way a person's age does, over the structured field of the company page. There is
 * deliberately no summary facet, because a filter would be a second door to the field §2 closed.
 *
 * Expertise is FREE TEXT matched against a company's own expertise labels — a typed box like the person
 * tab's, but holding SEVERAL terms, because a company is looked for by more than one capability at
 * once ("welding OR control systems"). A picker over the curated catalogue was the first attempt and
 * was
 * wrong on both counts a user notices: it rendered a different control from the pills beside it, and
 * typing into a searchable picker only filters its list, so the pill kept reading "Expertise" with
 * no value while the user thought they had set one. Matching on the label is also the closer reading
 * of §2, which speaks of the expertise LABELS rather than catalogue ids.
 *
 * There is NO name facet: the search box already matches the name at the highest weight, so a name
 * pill would be a narrower duplicate of the box sitting right above it.
 */
export const COMPANY_FIELDS: readonly FilterField[] = [
  { key: "companyIndustry", labelId: "search.fieldIndustry", kind: "multi", field: "industryIds", optionsKey: "industry", searchable: true },
  { key: "expertise", labelId: "search.fieldExpertise", kind: "textList", field: "expertises" },
  { key: "companySize", labelId: "search.fieldCompanySize", kind: "range", min: "employeeCountMin", max: "employeeCountMax", lo: COMPANY_SIZE_MIN, hi: COMPANY_SIZE_MAX },
];

// Industry and expertise are the two axes a company is actually looked for by; the name is usually
// typed into the search box instead, and size is a refinement.
export const COMPANY_FREQUENT_FIELD_KEYS: readonly string[] = ["companyIndustry", "expertise"];
